<script lang="ts">
  import { onMount } from 'svelte'
  import { api, type IpGroup, type IpRange } from '../lib/api'
  import { validateIpCidr } from '../lib/utils'
  import { notify } from '../lib/toast'
  import Modal from '../components/Modal.svelte'

  let groups = $state<IpGroup[]>([])
  let rangesMap = $state<Record<number, IpRange[]>>({})

  let addGroupOpen = $state(false), editGroupOpen = $state(false)
  let editingGroup = $state<IpGroup | null>(null)

  let newKey = $state(''), newName = $state(''), newDesc = $state(''), newAddrList = $state('VLESS_DOMAINS')
  let editKey = $state(''), editName = $state(''), editDesc = $state(''), editSort = $state(0), editAddrList = $state('VLESS_DOMAINS')

  let newIp = $state<Record<number, { addr: string; cmt: string }>>({})

  async function load() {
    groups = await api.listIpGroups()
    for (const g of groups) {
      rangesMap[g.id] = await api.listIpRanges({ group: g.id })
    }
  }
  onMount(load)

  async function handleAddGroup() {
    if (!/^[a-z_][a-z0-9_]*$/.test(newKey)) return notify('Неверный ключ', 'err')
    try {
      await api.createIpGroup({ key: newKey, name: newName || newKey, description: newDesc || undefined, addressList: newAddrList || 'VLESS_DOMAINS' })
      notify('Группа создана', 'ok')
      addGroupOpen = false; newKey = ''; newName = ''; newDesc = ''; newAddrList = 'VLESS_DOMAINS'
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  function openEdit(g: IpGroup) {
    editingGroup = g
    editKey = g.key; editName = g.name; editDesc = g.description || ''; editSort = g.sortOrder
    editAddrList = g.addressList || 'VLESS_DOMAINS'
    editGroupOpen = true
  }

  async function handleEditGroup() {
    if (!editingGroup) return
    try {
      await api.updateIpGroup(editingGroup.id, { key: editKey, name: editName, description: editDesc, sortOrder: editSort, addressList: editAddrList })
      notify('Сохранено', 'ok'); editGroupOpen = false; await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function handleDeleteGroup() {
    if (!editingGroup) return
    if (!confirm(`Удалить группу ${editingGroup.key}? Все её IP-диапазоны будут удалены.`)) return
    try { await api.deleteIpGroup(editingGroup.id); notify('Удалено', 'ok'); editGroupOpen = false; await load() }
    catch (e: any) { notify(e.message, 'err') }
  }

  async function addIp(groupId: number) {
    const d = newIp[groupId] || { addr: '', cmt: '' }
    if (!validateIpCidr(d.addr)) return notify('Неверный IP или CIDR', 'err')
    try {
      await api.createIpRange({ address: d.addr, groupId, comment: d.cmt || undefined })
      newIp[groupId] = { addr: '', cmt: '' }
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function delIp(id: number) {
    try { await api.deleteIpRange(id); await load() } catch (e: any) { notify(e.message, 'err') }
  }
</script>

<div class="card mb-4">
  <div class="flex items-center justify-between">
    <div>
      <h2 class="font-semibold">IP-диапазоны для MikroTik</h2>
      <p class="text-xs text-muted mt-1">
        Группы IP, которые попадают в firewall address-list. Используются в MikroTik-эндпоинтах.
      </p>
    </div>
    <button class="btn btn-primary" onclick={() => (addGroupOpen = true)}>+ Группа</button>
  </div>
</div>

<div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
  {#each groups as g (g.id)}
    <div class="card" title="key: {g.key}">
      <div class="flex justify-between items-start gap-2 mb-2">
        <button class="text-left flex-1 cursor-pointer min-w-0" onclick={() => openEdit(g)}>
          <div class="font-semibold truncate">{g.name}</div>
          {#if g.addressList && g.addressList !== 'VLESS_DOMAINS'}
            <span class="badge badge-red mt-1">→ {g.addressList}</span>
          {/if}
          {#if g.description}<p class="text-xs text-muted mt-1">{g.description}</p>{/if}
        </button>
        <div class="flex flex-col items-end gap-1 flex-shrink-0">
          <span class="badge badge-amber">{g._count?.ipRanges || 0} IP</span>
          <button class="btn-icon" onclick={() => openEdit(g)} title="Редактировать">✎</button>
        </div>
      </div>

      <div class="mt-3 pt-3 border-t border-bg-border">
        <div class="label">Диапазоны</div>
        <div class="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
          {#each (rangesMap[g.id] || []) as r}
            <span class="chip chip-amber" title={r.comment || ''}>
              {r.address}{#if r.comment}<span class="text-muted">·{r.comment}</span>{/if}
              <button class="chip-remove" onclick={() => delIp(r.id)}>×</button>
            </span>
          {/each}
        </div>
        <div class="flex gap-1 mt-2">
          <input class="input input-sm flex-1" placeholder="1.2.3.0/24"
                 value={newIp[g.id]?.addr || ''}
                 oninput={(e) => newIp[g.id] = { ...(newIp[g.id] || { addr:'', cmt:'' }), addr: (e.target as HTMLInputElement).value }}
                 onkeydown={(e) => e.key === 'Enter' && addIp(g.id)}>
          <input class="input input-sm w-24" placeholder="комментарий"
                 value={newIp[g.id]?.cmt || ''}
                 oninput={(e) => newIp[g.id] = { ...(newIp[g.id] || { addr:'', cmt:'' }), cmt: (e.target as HTMLInputElement).value }}>
          <button class="btn btn-secondary px-2 py-1 text-xs" onclick={() => addIp(g.id)}>+</button>
        </div>
      </div>
    </div>
  {/each}
</div>

{#if addGroupOpen}
  <Modal title="Новая группа IP" onclose={() => (addGroupOpen = false)}>
    <div class="space-y-3">
      <div><label class="label">Ключ (латиница)</label><input class="input" bind:value={newKey} placeholder="telegram_ips" autofocus></div>
      <div><label class="label">Название</label><input class="input" bind:value={newName} placeholder="Telegram IP ranges"></div>
      <div>
        <label class="label">MikroTik address-list</label>
        <input class="input" bind:value={newAddrList} placeholder="VLESS_DOMAINS">
        <p class="text-xs text-muted mt-1">VLESS_DOMAINS — через VPN. Другие (BLOCKED, ADS) — для своих правил в firewall.</p>
      </div>
      <div><label class="label">Описание (опц.)</label><input class="input" bind:value={newDesc}></div>
    </div>
    {#snippet footer()}
      <button class="btn btn-secondary" onclick={() => (addGroupOpen = false)}>Отмена</button>
      <button class="btn btn-primary" onclick={handleAddGroup}>Создать</button>
    {/snippet}
  </Modal>
{/if}

{#if editGroupOpen && editingGroup}
  <Modal title="Редактировать группу IP" onclose={() => (editGroupOpen = false)}>
    <div class="space-y-3">
      <div><label class="label">Ключ</label><input class="input" bind:value={editKey}></div>
      <div><label class="label">Название</label><input class="input" bind:value={editName}></div>
      <div>
        <label class="label">MikroTik address-list</label>
        <input class="input" bind:value={editAddrList}>
      </div>
      <div><label class="label">Описание</label><input class="input" bind:value={editDesc}></div>
      <div><label class="label">Порядок сортировки</label><input class="input" type="number" bind:value={editSort}></div>
    </div>
    {#snippet footer()}
      <button class="btn btn-danger mr-auto" onclick={handleDeleteGroup}>Удалить</button>
      <button class="btn btn-secondary" onclick={() => (editGroupOpen = false)}>Отмена</button>
      <button class="btn btn-primary" onclick={handleEditGroup}>Сохранить</button>
    {/snippet}
  </Modal>
{/if}
